import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

/**
 * Dedicated high-z pane for selection/search overlays. Sits above the shared
 * overlay pane (z 400) and the marker pane (z 600), below tooltips (z 650),
 * so highlights are never buried under data layers. pointer-events is
 * disabled so overlays never intercept clicks meant for the feature beneath.
 */
export const SELECTION_PANE = 'selection-pane'

export function ensureSelectionPane(map: L.Map): void {
  if (!map.getPane(SELECTION_PANE)) {
    const pane = map.createPane(SELECTION_PANE)
    pane.style.zIndex = '640'
    pane.style.pointerEvents = 'none'
  }
}

interface SelectionHaloProps {
  /** GeoJSON geometry of the selected feature (captured at click time). */
  geometry: GeoJSON.Geometry | null
}

/**
 * Marks the currently selected point feature with a contrasting double ring
 * (white casing + blue ring) plus a brief pulse. Driven purely by React
 * state, so it survives the version-keyed GeoJSON layer remounts that make
 * imperative layer.setStyle highlighting unviable. Line features (feeders,
 * conductors) are highlighted via the layer style callbacks instead, so
 * non-Point geometries are ignored here.
 */
export function SelectionHalo({ geometry }: SelectionHaloProps) {
  const map = useMap()

  useEffect(() => {
    if (!geometry || geometry.type !== 'Point') return
    ensureSelectionPane(map)
    // preferCanvas on the MapContainer (D1) makes Leaflet default custom
    // panes to Canvas too, which ignores `className` — the .pulse-marker
    // animation would silently stop. Force this pane's rings back onto an
    // explicit SVG renderer; created fresh per selection and removed in
    // cleanup alongside layerGroup so it never leaks an empty <svg>.
    const renderer = L.svg({ pane: SELECTION_PANE })
    const [lng, lat] = geometry.coordinates
    const latLng = L.latLng(lat, lng)
    const layerGroup = L.layerGroup().addTo(map)

    // White casing ring — contrast against any basemap or layer color
    L.circleMarker(latLng, {
      pane: SELECTION_PANE,
      renderer,
      radius: 14,
      color: '#ffffff',
      weight: 5,
      fill: false,
      opacity: 0.95,
      interactive: false,
    }).addTo(layerGroup)

    // Blue selection ring on top of the casing
    L.circleMarker(latLng, {
      pane: SELECTION_PANE,
      renderer,
      radius: 14,
      color: '#2563eb',
      weight: 2.5,
      fill: false,
      opacity: 1,
      interactive: false,
    }).addTo(layerGroup)

    // Brief pulse to draw the eye on selection; removed after two cycles.
    // .pulse-marker carries the transform-box fix from index.css.
    const pulse = L.circleMarker(latLng, {
      pane: SELECTION_PANE,
      renderer,
      radius: 14,
      color: '#2563eb',
      weight: 2,
      fill: false,
      opacity: 0.8,
      interactive: false,
      className: 'pulse-marker',
    }).addTo(layerGroup)
    const pulseTimer = window.setTimeout(() => {
      layerGroup.removeLayer(pulse)
    }, 3000)

    return () => {
      window.clearTimeout(pulseTimer)
      layerGroup.remove()
      renderer.remove()
    }
  }, [geometry, map])

  return null
}
